// Light / dark / system theme with cross-tab sync.
//
// The preference lives in localStorage `ui-theme` ('light' | 'dark' | 'system'). public/theme-init.js
// paints it before React loads (no flash). This module keeps every tab in step:
//  - `storage` event: another tab changed it (never fires in the writing tab),
//  - same-tab CustomEvent `ui-theme-change`: this tab changed it (several hooks/components listen),
//  - `pageshow`: a bfcache-restored page skips theme-init.js, so re-apply,
//  - matchMedia change: the OS switched while the preference is 'system'.
import { useSyncExternalStore } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'ui-theme'
export const THEME_EVENT = 'ui-theme-change'

const isPref = (v: unknown): v is ThemePreference => v === 'light' || v === 'dark' || v === 'system'

export function readThemePreference(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return isPref(v) ? v : 'system'
  } catch { return 'system' }
}

export function systemPrefersDark(): boolean {
  try { return window.matchMedia('(prefers-color-scheme: dark)').matches } catch { return false }
}

export function resolveTheme(pref: ThemePreference, prefersDark = systemPrefersDark()): ResolvedTheme {
  return pref === 'dark' || (pref === 'system' && prefersDark) ? 'dark' : 'light'
}

/** Paint a preference onto <html> (class `dark`, data-theme, color-scheme meta). */
export function applyTheme(pref: ThemePreference = readThemePreference()): ResolvedTheme {
  const resolved = resolveTheme(pref)
  if (typeof document === 'undefined') return resolved
  const root = document.documentElement
  // Suppress transitions for the frame the theme flips, so borders/backgrounds don't animate.
  root.classList.add('theme-switching')
  root.classList.toggle('dark', resolved === 'dark')
  root.dataset.theme = pref
  root.style.colorScheme = resolved
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('theme-switching')))
  return resolved
}

/** Change the preference: persists, repaints and notifies this tab (other tabs get `storage`). */
export function setThemePreference(pref: ThemePreference): void {
  if (!isPref(pref)) return
  let persisted = true
  try { localStorage.setItem(THEME_STORAGE_KEY, pref) } catch { persisted = false }
  memoryPref = persisted ? null : pref   // storage blocked (private mode): remember for this tab
  applyTheme(pref)
  notify()
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: pref }))
}

/** Flip between light and dark (from whatever is showing now). */
export function toggleTheme(): void {
  setThemePreference(resolveTheme(currentPreference()) === 'dark' ? 'light' : 'dark')
}

let memoryPref: ThemePreference | null = null
function currentPreference(): ThemePreference { return memoryPref ?? readThemePreference() }

// ─── subscription (one set of window listeners shared by every hook) ────────────────────
type Snapshot = { preference: ThemePreference; resolved: ResolvedTheme }
let snapshot: Snapshot = { preference: 'system', resolved: 'light' }
const subscribers = new Set<() => void>()
let installed = false

function computeSnapshot(): Snapshot {
  const preference = currentPreference()
  return { preference, resolved: resolveTheme(preference) }
}
function refreshSnapshot(): Snapshot {
  const next = computeSnapshot()
  if (next.preference !== snapshot.preference || next.resolved !== snapshot.resolved) snapshot = next
  return snapshot
}
function notify() {
  refreshSnapshot()
  subscribers.forEach(fn => fn())
}

/** Install the global listeners once (idempotent). Shells call it; useTheme() calls it too. */
export function installThemeSync(): () => void {
  if (installed || typeof window === 'undefined') return () => {}
  installed = true
  refreshSnapshot()
  const onStorage = (e: StorageEvent) => {
    if (e.key !== null && e.key !== THEME_STORAGE_KEY) return
    memoryPref = null
    applyTheme(readThemePreference()); notify()
  }
  const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) { applyTheme(currentPreference()); notify() } }
  const onCustom = () => notify()
  let mq: MediaQueryList | null = null
  const onMedia = () => { if (currentPreference() === 'system') { applyTheme('system'); notify() } }
  window.addEventListener('storage', onStorage)
  window.addEventListener('pageshow', onPageShow)
  window.addEventListener(THEME_EVENT, onCustom)
  try { mq = window.matchMedia('(prefers-color-scheme: dark)'); mq.addEventListener('change', onMedia) } catch { /* old browsers */ }
  // Re-assert what theme-init.js painted (it may have been skipped).
  applyTheme(currentPreference())
  return () => {
    installed = false
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('pageshow', onPageShow)
    window.removeEventListener(THEME_EVENT, onCustom)
    mq?.removeEventListener('change', onMedia)
  }
}

function subscribe(fn: () => void) {
  installThemeSync()
  subscribers.add(fn)
  return () => { subscribers.delete(fn) }
}
const getSnapshot = () => (installed ? snapshot : refreshSnapshot())
const getServerSnapshot = (): Snapshot => ({ preference: 'system', resolved: 'light' })

/** `const { preference, resolved, setTheme } = useTheme()` — re-renders on any change, in any tab. */
export function useTheme(): Snapshot & { setTheme: (p: ThemePreference) => void; toggle: () => void } {
  const s = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  return { ...s, setTheme: setThemePreference, toggle: toggleTheme }
}
